import React, { useEffect, useRef } from 'react';
import { Box, Flex, Text, VStack, IconButton, Tooltip, Badge, useColorModeValue } from '@chakra-ui/react';
import { Play, Pause, Download } from 'lucide-react';
import { downloadFile } from '../../api/quranService';

export const BISMILLAH = 'بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ';

const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
export const toArabicDigits = (n) => String(n).replace(/\d/g, (d) => ARABIC_DIGITS[d]);

/**
 * One ayah: Arabic text with its end-of-ayah marker, the translation, and play/download buttons
 * when a recording exists for the chosen reciter.
 */
const AyahCard = ({ ayah, settings, recording, isActive, isPlaying, onPlay, downloadName }) => {
  const ref = useRef(null);
  const activeBg = useColorModeValue('teal.50', 'teal.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtextColor = useColorModeValue('gray.600', 'gray.300');

  // Follow along: keep the ayah being recited in view
  useEffect(() => {
    if (isActive && ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [isActive]);

  return (
    <Flex
      ref={ref} id={`ayah-${ayah.ayah_key}`}
      gap={{ base: 3, md: 5 }} py={6} px={{ base: 2, md: 4 }}
      borderBottom="1px" borderColor={borderColor}
      bg={isActive ? activeBg : 'transparent'} borderRadius={isActive ? 'lg' : 'none'}
      transition="background 0.2s"
    >
      <VStack spacing={2} flexShrink={0} w="44px">
        <Badge variant="subtle" colorScheme="teal" fontSize="xs">{ayah.ayah_key}</Badge>
        {recording && (
          <>
            <Tooltip label={isPlaying ? 'Pause' : 'Play from here'}>
              <IconButton
                size="sm" variant={isActive ? 'solid' : 'ghost'} colorScheme="teal" borderRadius="full"
                aria-label={isPlaying ? 'Pause' : `Play ayah ${ayah.ayah_key}`}
                icon={isPlaying ? <Pause size={16} /> : <Play size={16} />}
                onClick={onPlay}
              />
            </Tooltip>
            <Tooltip label="Download this ayah">
              <IconButton
                size="sm" variant="ghost" borderRadius="full" aria-label={`Download ayah ${ayah.ayah_key}`}
                icon={<Download size={16} />}
                onClick={() => downloadFile(recording.mediaKey, downloadName)}
              />
            </Tooltip>
          </>
        )}
      </VStack>

      <Box flex={1} minW={0}>
        <Text
          dir="rtl" textAlign="right" fontFamily={settings.fontFamily} fontSize={{ base: '2xl', md: settings.arabicSize }}
          lineHeight="2.1" mb={settings.showTranslation ? 3 : 0}
        >
          {ayah.text}
          <Text as="span" mx={2} fontSize="0.8em" color="teal.600" whiteSpace="nowrap">
            ﴿{toArabicDigits(ayah.ayah_index)}﴾
          </Text>
        </Text>
        {settings.showTranslation && ayah.translations?.text && (
          <Text dir="auto" color={subtextColor} fontSize="md" lineHeight="tall">{ayah.translations.text}</Text>
        )}
      </Box>
    </Flex>
  );
};

export default AyahCard;
